import { createSlice } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../store';
import axios from '../../resources/axios';
import { Admin, AdminInfo, CreateAdminBody } from '../../resources/adminsTypes';
import { getLocalStorage, setLocalStorage } from '../../resources/functions';
import { STORE_CURRENT_CITY } from '../../resources/storageVariables';
import { signOut } from './authSlice';

interface AdminsState {
  items: Admin[];
  info?: AdminInfo;
  currentCity?: string;
  isGoogleApiInit: boolean;
}

const initialState: AdminsState = {
  items: [],
  isGoogleApiInit: false,
  currentCity: getLocalStorage(STORE_CURRENT_CITY) || undefined
};

const adminsSlice = createSlice({
  name: 'admins',
  initialState,
  reducers: {
    getAdmins: (state, action) => {
      state.items = action.payload.admins;
    },
    getAdminInfo: (state, action) => {
      console.log('🔍 adminsSlice getAdminInfo - payload:', action.payload);
      console.log('🔍 admin object:', action.payload.admin);
      console.log('🔍 admin.cities:', action.payload.admin.cities);
      console.log('🔍 admin.workshops:', action.payload.admin.workshops);
      console.log('🔍 admin.type:', action.payload.admin.type);
      
      // Use stored currentCity if exists and is valid for this admin, otherwise use first city
      const storedCity = state.currentCity;
      const adminCities = action.payload.admin.cities || [];
      const isStoredCityValid = storedCity && adminCities.some((city: { _id: string }) => city._id === storedCity);
      
      // Determine currentCity: try stored, then first city, then first workshop city
      let currentCity = isStoredCityValid ? storedCity : adminCities[0]?._id;
      console.log('🔍 currentCity from cities:', currentCity);
      
      // For Masters/Workers who might not have cities array but have workshops
      if (!currentCity && action.payload.admin.workshops?.length > 0) {
        const workshop = action.payload.admin.workshops[0];
        console.log('🔍 First workshop:', workshop);
        console.log('🔍 workshop.city:', workshop.city);
        console.log('🔍 workshop.cityId:', workshop.cityId);
        currentCity = workshop.city?._id || workshop.cityId;
        console.log('🔍 currentCity from workshop:', currentCity);
      }
      
      state.info = action.payload.admin;
      state.currentCity = currentCity;
      
      console.log('✅ Final currentCity set in state:', currentCity);
      
      // Only set city header if currentCity exists
      if (currentCity) {
        axios.defaults.headers.common['city'] = currentCity;
        setLocalStorage(STORE_CURRENT_CITY, currentCity);
        console.log('✅ City header set:', currentCity);
      } else {
        console.warn('⚠️ No currentCity - city header NOT set');
      }
    },
    setCurrentCity: (state, action) => {
      const currentCity = action.payload;
      state.currentCity = currentCity;
      axios.defaults.headers.common['city'] = currentCity;
      setLocalStorage(STORE_CURRENT_CITY, currentCity);
    },
    initGoogleApi: state => {
      state.isGoogleApiInit = true;
    }
  }
});

export const {
  getAdmins,
  getAdminInfo,
  setCurrentCity,
  initGoogleApi
} = adminsSlice.actions;

export const initGoogleApiAsync = (): AppThunk => async dispatch => {
  const apiUrl =
    'https://maps.googleapis.com/maps/api/js?key=AIzaSyBu5VqKowtFVHsHfMDxdvvspcbjzztTvSA&libraries=places,visualization,geometry';

  // Видаляємо старі скрипти Google Maps без geometry library
  const oldScripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
  oldScripts.forEach(script => {
    if (!script.getAttribute('src')?.includes('geometry')) {
      script.remove();
    }
  });

  if (!document.querySelectorAll(`[src="${apiUrl}"]`).length) {
    document.body.appendChild(
      Object.assign(document.createElement('script'), {
        type: 'text/javascript',
        src: apiUrl,
        onload: () => dispatch(initGoogleApi())
      })
    );
  } else {
    dispatch(initGoogleApi());
  }
};

export const getAdminsAsync = (cityIds = ''): AppThunk => async dispatch => {
  const response = await axios.get(`/admins${cityIds ? `?cities=${cityIds}` : ''}`);
  dispatch(getAdmins(response.data));
};

export const getAdminAsync = (adminId: string): AppThunk => async () => {
  const response = await axios.get(`/admins/${adminId}`);
  return response.data;
};

export const createAdminAsync = (
  body: CreateAdminBody
): AppThunk => async () => {
  await axios.post('/admins', body);
};

export const updateAdminAsync = (
  adminId: string,
  body: CreateAdminBody
): AppThunk => async () => {
  await axios.put(`/admins/${adminId}`, body);
};

export const deleteAdminAsync = (adminId: string): AppThunk => async () => {
  await axios.delete(`/admins/${adminId}`);
};

export const getAdminInfoAsync = (): AppThunk => async dispatch => {
  try {
    const response = await axios.get('/admins/info');
    dispatch(getAdminInfo(response.data));
  } catch (e: any) {
    if (e.response?.status === 401) {
      dispatch(signOut());
    }
  }
};

export const selectAdmins = (state: RootState) => state.admins;

export default adminsSlice.reducer;
